import React, { useRef } from 'react'
import { Upload, FileText, X, Loader2, AlertCircle, Info } from 'lucide-react'
import { ParentInput } from '../types'

interface Props {
  files: File[]
  parentInput: ParentInput
  onFilesSelected: (files: File[]) => void
  onFileRemoved: (index: number) => void
  onParentInputChange: (input: Partial<ParentInput>) => void
  onSubmit: () => void
  isProcessing: boolean
  error: string | null
}

export default function UploadStep({
  files,
  parentInput,
  onFilesSelected,
  onFileRemoved,
  onParentInputChange,
  onSubmit,
  isProcessing,
  error
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type === 'application/pdf' || file.type.startsWith('text/')
    )
    onFilesSelected(droppedFiles)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFilesSelected(Array.from(e.target.files))
    }
  }

  const canSubmit = files.length > 0 && parentInput.childName.trim() !== '' && !isProcessing

  return (
    <div className="space-y-6">
      {/* Important notice */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">
          This tool summarises what is already written in your report. It does not add clinical advice or diagnosis.
        </p>
      </div>

      {/* File Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Upload Diagnostic Reports
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload your child's diagnostic assessments, EHCP documents, therapy reports, or clinical letters
          </p>
        </div>

        <div className="p-6">
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
          >
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-gray-500 mt-1">
              PDF files supported
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onFileRemoved(index)
                    }}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Parent Input Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            About Your Child
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Help us personalise the briefing pack
          </p>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Child's first name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={parentInput.childName}
              onChange={(e) => onParentInputChange({ childName: e.target.value })}
              placeholder="Enter your child's first name"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What helps your child come alive? (optional)
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Share what lights them up - special interests, favourite activities, things that make them happy
            </p>
            <textarea
              value={parentInput.comeAlive}
              onChange={(e) => onParentInputChange({ comeAlive: e.target.value })}
              placeholder="e.g., He lights up when talking about trains, loves being outside in nature, gets excited about building things with LEGO..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional notes (optional)
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Anything important not captured in the reports? Current strategies, recent changes, things that work at home?
            </p>
            <textarea
              value={parentInput.additionalNotes}
              onChange={(e) => onParentInputChange({ additionalNotes: e.target.value })}
              placeholder="e.g., We've found that giving 10-minute warnings before transitions really helps. She's recently started using a visual schedule..."
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Something went wrong</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={onSubmit}
        disabled={!canSubmit}
        className={`
          w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2
          ${canSubmit
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }
        `}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating your briefing pack...
          </>
        ) : (
          'Generate Nanny Briefing Pack'
        )}
      </button>

      {isProcessing && (
        <p className="text-sm text-center text-gray-500">
          This may take a minute. We're carefully extracting information from your reports.
        </p>
      )}
    </div>
  )
}
